import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { isSafariBrowser } from './utilities'
import { lenis } from './lenis'
import lottieWeb from 'lottie-web'
gsap.registerPlugin(ScrollTrigger)

export let Animations,
  Preloader,
  Announcement,
  Banner,
  BannerScroll,
  Headings,
  Highlight,
  Collage,
  Footer,
  Lottie,
	LottieInview,
  Chris,
  PageBanner,
  StickyContent,
  ParallaxImages

Preloader = () => {
  const banner = document.querySelector('.banner')
  const preloader = document.querySelector('.preloader')
  const animation = preloader?.querySelector('.preloader__animation')
  const loadingBar = preloader?.querySelector('.preloader__loading span')

  if (!preloader) return

  lenis.stop()

  const timeline = gsap.timeline({
    delay: 0.5,
    paused: true,
    onComplete: () => {
      preloader.remove()
      lenis.start()
    },
  })

  const anim = lottieWeb.loadAnimation({
    container: animation,
    loop: false,
    autoplay: false,
    renderer: 'svg',
    path: '/lottie/loader.json',
    rendererSettings: {
      progressiveLoad: true,
    },
  })

  anim.addEventListener('data_ready', () => {
    timeline.play()
  })

  timeline.add(() => anim.playSegments([0, 79], true))
  timeline.to(
    loadingBar,
    {
      duration: 3,
      ease: 'power1',
      width: '100%',
      onUpdate: function () {
        loadingBar.setAttribute('data-count', Math.round(this.progress() * 100))
      },
    },
    '<'
  )
  timeline.to(preloader, {
    duration: 1.8,
    clipPath: 'inset(0 0 100% 0)',
    ease: 'power4.inOut',
  })
  timeline.to(
    [loadingBar, animation],
    {
      duration: 0.6,
      opacity: 0,
    },
    '<'
  )
  if (banner) {
    timeline.add(() => {
      Banner()
    }, '<')
  }
}

Banner = () => {
  const banner = document.querySelector('.banner')
  const image = banner?.querySelector('.banner__image')
  const heading = banner?.querySelectorAll('.banner__heading .line')

  if (!banner) return

  const timeline = gsap.timeline()

  timeline.from(image, {
    duration: 5,
    scale: 1.25,
    ease: 'power2',
  })

  timeline.from(
    heading,
    {
      duration: 1.6,
      yPercent: 110,
      stagger: 0.2,
      ease: 'expo',
    },
    1
  )
}

Headings = () => {
  const headings = document.querySelectorAll('[data-anim="heading"]')

  headings.forEach((el) => {
    const lines = el.querySelectorAll('.line')
    gsap.from(lines, {
      duration: 1.6,
      yPercent: 115,
      stagger: 0.05,
      delay: 0.6,
      ease: 'expo',
      scrollTrigger: {
        trigger: el,
        start: 'top 80%',
        once: true,
      },
    })
  })
}

Announcement = () => {
  const bar = document.querySelector('.announcement')
  const inner = bar?.querySelector('.announcement__track')

  if (!bar) return

  const marquee = gsap.to(inner, {
    duration: 30,
    xPercent: -25,
    repeat: -1,
    ease: 'none',
  })

  bar.addEventListener('mouseenter', () => marquee.pause())
  bar.addEventListener('mouseleave', () => marquee.play())
}

BannerScroll = () => {
  const el = document.querySelector('.banner')
  const image = el?.querySelector('.banner__image')
  const video = el?.querySelector('.banner__video')
  const promoImage = el?.querySelector('.banner__promo-image')
  const content = el?.querySelector('.banner__content')

  if (!el || window.innerWidth <= 650) return

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: el,
      // pin: true,
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    },
  })
  timeline.to([content, video, promoImage], {
    duration: 0.25,
    opacity: 0,
  })
  timeline.to(
    image,
    {
      duration: 1,
      scale: 1.25,
      opacity: 0,
      ease: 'power2',
    },
    '<'
  )
  timeline.to(el, {
    duration: 0.1,
    autoAlpha: 0,
  })
}

Highlight = () => {
  const texts = document.querySelectorAll('[data-highlight]')

  texts.forEach((text) => {
    const chars = text.querySelectorAll('.char')

    gsap.to(chars, {
      opacity: 1,
      stagger: 0.2,
      ease: 'none',
      scrollTrigger: {
        trigger: text,
        start: 'top 85%',
        end: 'bottom 40%',
        scrub: true,
      },
    })
  })
}

Collage = () => {
  const els = document.querySelectorAll('.collage')

  if (!els || window.innerWidth <= 1024) return

  els.forEach((el) => {
    const layers = el.querySelectorAll('.media')
    const center = el.querySelector('.collage__center')
    const centerImage = el.querySelector('.collage__main-image')
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: center,
        pin: el,
        start: 'center center',
        end: '+=2500',
        scrub: 0.5,
      },
    })
    timeline.to(centerImage, {
      duration: 1,
      width: '100vw',
      height: '100vh',
      ease: 'power2',
    })
    timeline.to(
      layers,
      {
        duration: 1,
        scale: 4,
        ease: 'power2',
      },
      '<'
    )
  })
}

Footer = () => {
  const footer = document.querySelector('.footer__inner')

  if (window.innerWidth <= 1024) return

  gsap.fromTo(
    footer,
    {
      clipPath:
        'polygon(41.25rem 0%, calc(100% - 41.25rem) 0%, calc(100% - 15.625rem) 0rem, 100% 0rem, 100% 100%, 0 100%, 0% 0rem, 15.625rem 0rem)',
    },
    {
      clipPath:
        'polygon(41.25rem 0%, calc(100% - 41.25rem) 0%, calc(100% - 15.625rem) 15.625rem, 100% 42.625rem, 100% 100%, 0 100%, 0% 42.625rem, 15.625rem 15.625rem)',
      ease: 'power1',
      scrollTrigger: {
        trigger: footer,
        start: 'top 95%',
        end: 'bottom bottom',
        scrub: 1,
      },
    }
  )
}

Lottie = () => {
  const animations = document.querySelectorAll('[data-lottie-play]')

  animations.forEach((container) => {
    const name = container.dataset.lottiePlay

    ScrollTrigger.create({
      trigger: container,
      start: 'top top',
      once: true,
      onEnter: () => {
        const anim = lottieWeb.loadAnimation({
          container: container,
          loop: true,
          autoplay: true,
          renderer: 'svg',
          path: '/lottie/' + name + '.json',
          rendererSettings: {
            progressiveLoad: true,
          },
        })
      },
    })
  })
}

LottieInview = () => {
  const animations = document.querySelectorAll('[data-lottie-inview]')

  animations.forEach((container) => {
    const name = container.dataset.lottieInview

    ScrollTrigger.create({
      trigger: container,
      start: 'top 70%',
      once: true,
      onEnter: () => {
        const anim = lottieWeb.loadAnimation({
          container: container,
          loop: false,
          autoplay: true,
          renderer: 'svg',
          path: '/lottie/' + name + '.json',
          rendererSettings: {
            progressiveLoad: true,
          }
        })
      },
    })
  })
}

Chris = () => {
  const el = document.querySelector('.chris')
  const text = el?.querySelector('.chris__content')
  const chars = el?.querySelectorAll('.char')
  const illustrationHolder = el?.querySelector('.chris__illustration-holder')
  const layer = el?.querySelectorAll('.chris__layer')
  const annotations = el?.querySelectorAll('.chris__annotation')
  const animationHolder = el?.querySelector('.chris__animation-holder')
  const animationEl = el?.querySelector('[data-lottie]')

  if (!el) return

  const animation = lottieWeb.loadAnimation({
    container: animationEl,
    loop: false,
    autoplay: false,
    renderer: 'svg',
    path: '/lottie/' + animationEl.dataset.lottie + '.json',
    rendererSettings: {
      progressiveLoad: true,
    },
  })

  gsap.to(chars, {
    opacity: 1,
    stagger: 0.2,
    ease: 'none',
    scrollTrigger: {
      trigger: el,
      endTrigger: text,
      start: 'top 1%',
      end: 'bottom 30%',
      scrub: true,
    },
  })

  if (window.innerWidth > 1025) {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: illustrationHolder,
        pin: true,
        pinType: 'fixed',
        start: 'top 20%',
        end: `+=${1000}`,
        scrub: 1.2,
      },
    })
    tl.to(
      layer,
      {
        y: 0,
        scale: 1,
        yPercent: 0,
        duration: 10,
      },
      '<'
    )
    tl.fromTo(
      annotations,
      {
        opacity: 0,
        duration: 2.5,
      },
      {
        opacity: 1,
        stagger: 2,
      },
      '-=6'
    )
  }

  const frameCount = 624
  const progress = { frame: 0 }

  gsap.to(progress, {
    frame: frameCount - 1,
    snap: 'frame',
    ease: 'none',
    scrollTrigger: {
      trigger: animationHolder,
      pin: true,
      pinType: 'fixed',
      start: 'top 30%',
      end: window.innerWidth < 650 ? `+=${4000}` : `+=${6000}`,
      scrub: 1.5,
    },
    onUpdate: render,
  })

  function render() {
    animation.goToAndStop(progress.frame, true)
  }
}

PageBanner = () => {
  const el = document.querySelector('.page-banner')
  const text = el?.querySelector('.page-banner__content')
  const chars = el?.querySelectorAll('.char')

  if (!el || !chars.length) return

  gsap.to(chars, {
    opacity: 1,
    stagger: 0.2,
    ease: 'none',
    scrollTrigger: {
      trigger: el,
      endTrigger: text,
      start: 'top 5%',
      end: 'bottom 40%',
      scrub: true,
    },
  })
}

StickyContent = () => {
  const el = document.querySelector('[data-sticky]')
  const content = el?.querySelector('[data-sticky-content]')
  const progress = el?.querySelector('[data-sticky-progress] span')

  if (!el || window.innerWidth <= 1024) return

  gsap.to(progress, {
    scrollTrigger: {
      trigger: content,
      pin: true,
      start: 'top +=300px',
      endTrigger: el,
      end: 'bottom bottom',
      pinSpacing: false,
      scrub: true,
    },
    height: '100%',
  })
}

ParallaxImages = () => {
  const holders = document.querySelectorAll('[data-image-parallax]')

  if (!holders.length) return

  holders.forEach((holder) => {
    const image = holder.querySelectorAll('img, iframe, video')
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: holder,
        start: 'top bottom',
        end: 'center center',
        scrub: true,
      },
    })
    tl.fromTo(
      image,
      {
        yPercent: window.innerWidth <= 650 ? -15 : -35,
        scale: window.innerWidth <= 650 ? 1.05 : 1.1,
      },
      {
        yPercent: 0,
        scale: 1,
      }
    )
  })
}

Animations = () => {
  Preloader()
  PageBanner()
  BannerScroll()
  Headings()
  Highlight()
  Collage()
  Lottie()
  Chris()
  StickyContent()
  Footer()
  ParallaxImages()
	LottieInview()
}

export default () => {}
